import { useCallback } from 'react';
import styles from './ComparePage.module.css';
import { useCompare } from './CompareContext';
import CompareCard from '../../components/CompareCard/CompareCard';
import Header from '../../components/Header/Header';
import TotalObjects from '../../components/TotalObjects/TotalObjects';
import { PropertyData } from '../../components/CardsComponents/types';
import Footer from '../../components/Footer/Footer';
import CompareNoObjectsImage from '../../assets/images/CompareNoObjetcs.webp';
import Button from '../../components/UI/Button/Button';
import cn from 'classnames';
import { Link } from 'react-router-dom';
import { PROJECTS_PAGE } from '../../router/paths';
import { Helmet } from 'react-helmet';

interface CompareElementProps {
  compares: PropertyData[];
  title: string;
  type: 'flat' | 'parking' | 'business';
  showTitleAndLine: boolean;
  onRemove: (card: { id: number; type: string }) => void;
  onRemoveAll: () => void;
}

const CompareElement = ({
  compares,
  title,
  type,
  showTitleAndLine,
  onRemove,
  onRemoveAll,
}: CompareElementProps) => {
  const compare = compares.filter((item) => item.type === type);

  if (compare.length === 0) return null;

  return (
    <div
      className={cn(styles.track, {
        [styles.track_start]: compare.length >= 5,
      })}
    >
      {showTitleAndLine && (
        <>
          <h3>{title}</h3>
          <span
            className={cn(styles.line, {
              [styles.line_hidden]: compare.length === 1,
            })}
          ></span>
        </>
      )}
      {compare.length === 1 ? (
        <p
          className={cn('text-2_font', styles.more_obj, {
            [styles.more_obj_margin]: compare.length === 1,
          })}
        >
          Сравнить пока не&nbsp;получится&nbsp;&mdash; для сравнения нужно добавить больше объектов.
        </p>
      ) : (
        <TotalObjects objectLength={compare.length} buttonColor={'blue'} onClick={onRemoveAll} />
      )}
      <div
        className={cn(styles.compare_cards, {
          [styles.compare_cards_center]: compare.length === 1,
          [styles.compare_cards_start]: compare.length > 1,
        })}
        data-count={compare.length}
      >
        {compare.map((card, index) => (
          <CompareCard
            key={`${('roomType' in card ? card.roomType : card.type || 'object')}-${card.area}`}
            data={card}
            isFirst={index === 0}
            isLast={compare.length - 1 === index}
            onClick={() => onRemove({ id: card.id, type: card.type || 'flat' })}
            isOne={compare.length === 1}
          />
        ))}
      </div>
    </div>
  );
};

export default function ComparePage() {
  const { compares, removeFromCompares, removeAllByType } = useCompare();

  const removeCard = useCallback(
    ({ id, type }: { id: number; type: string }) => {
      const cardToRemove = compares.find((card) => card.id === id && card.type === type);
      if (cardToRemove) {
        removeFromCompares(cardToRemove);
      }
    },
    [removeFromCompares, compares]
  );

  const removeAllByTypeHandler = useCallback(
    (type: 'flat' | 'parking' | 'business') => {
      removeAllByType(type);
    },
    [removeAllByType]
  );

  const hasOtherTypes = compares.some((item) => item.type !== 'flat');

  return (
    <>
      <Helmet>
        <title>Сравнение | Восток Строй</title>
        <meta name="description" content="Мы начали свой путь в 2014 году и реализовали 9 успешных проектов." />
        <meta name="keywords" content="недвижимость, девелопер, Каспийск, новостройки, квартиры" />
        <meta name="author" content="Восток Строй" />
        <meta name="robots" content="noindex, nofollow" />
        <link rel="canonical" href="https://vostokstroy.com/contacts" />

        <meta property="og:title" content="Сравнение | Восток Строй" />
        <meta property="og:description" content="Мы начали свой путь в 2014 году и реализовали 9 успешных проектов." />
        <meta property="og:image" content="https://vostokstroy.com/open-graphs/main-opengraph.jpg" />
        <meta property="og:url" content="https://vostokstroy.com/contacts" />
        <meta property="og:type" content="website" />
        <meta property="og:site_name" content="Восток Строй" />

        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Сравнение | Восток Строй" />
        <meta name="twitter:description" content="Мы начали свой путь в 2014 году и реализовали 9 успешных проектов." />
        <meta name="twitter:image" content="https://vostokstroy.com/open-graphs/main-opengraph.jpg" />

        <link rel="icon" type="image/svg+xml" href="/favicons/main-fav-32x32.svg" />
        <link rel="icon" type="image/png" sizes="16x16" href="/favicons/main-fav-16x16.png" />
        <link rel="icon" type="image/png" sizes="32x32" href="/favicons/main-fav-32x32.png" />
        <link rel="apple-touch-icon" sizes="180x180" href="/favicons/main-apple-touch-icon-180x180.png" />
        <link rel="icon" type="image/png" sizes="180x180" href="/favicons/main-fav-180x180.png" />
      </Helmet>
      <div className={styles.compare}>
        <Header theme='light' />
        <div className={styles.wrapper}>
          <h2 className={styles.page_title}>Сравнение</h2>
          {compares.length > 0 ? (
            <>
              <CompareElement
                title='Квартиры'
                compares={compares}
                type='flat'
                showTitleAndLine={hasOtherTypes}
                onRemove={removeCard}
                onRemoveAll={() => removeAllByTypeHandler('flat')}
              />
              <CompareElement
                title='Помещения для бизнеса'
                compares={compares}
                type='business'
                showTitleAndLine={true}
                onRemove={removeCard}
                onRemoveAll={() => removeAllByTypeHandler('business')}
              />
              <CompareElement
                title='Машино-места'
                compares={compares}
                type='parking'
                showTitleAndLine={true}
                onRemove={removeCard}
                onRemoveAll={() => removeAllByTypeHandler('parking')}
              />
            </>
          ) : (
            <div className={styles.no_objects_wrapper}>
              <div className={styles.no_objects_text}>
                <p className='text_body_b2'>
                  На&nbsp;этой странице удобно сравнивать характеристики объектов. Сейчас здесь ничего нет, но&nbsp;достаточно нажать на&nbsp;кнопку &laquo;Сравнить&raquo; в&nbsp;карточке объекта и&nbsp;тут появятся его детали.
                </p>
              </div>
              <div className={styles.no_objects_image}>
                <img src={CompareNoObjectsImage} alt='Пример нажатой кнопки "Добавить в сравнение"' />
              </div>
              <Link to={PROJECTS_PAGE}>
                <Button roundType='small_rounded' colorType='gray'>
                  К проектам
                </Button>
              </Link>
            </div>
          )}
        </div>
        <Footer theme='light' />
      </div>
    </>
  );
}
